import React, { useEffect, useState, useCallback } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Cookies from 'js-cookie'
import {
  usePageDesignerEditMode,
  useConfig,
  useNavigation
} from 'msp-integrations'
import { PopupContentProps } from './PopupContent.types'
import Modal from '@molecules/Modal/Modal'
import Image from '@atoms/Image/Image'
import Headline from '@atoms/Headline/Headline'
import { Button } from '@atoms/Button'

const PopupContent = ({
  data: { openDelay, imageAlt, headline, description, buttonTitle, image },
  custom
}: PopupContentProps) => {
  const url = custom?.content?.url ?? undefined
  const pageDesignerEditMode = usePageDesignerEditMode()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalWillOpen, setModalWillOpen] = useState(false)
  const { hash, pathname } = useLocation()
  const history = useHistory()
  const navigate = useNavigation()
  const { cookiePrefix } = useConfig()
  const cookieName = `${cookiePrefix}PopupContent`

  const openModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const setCookie = useCallback(
    (value: boolean) => {
      if (!Cookies.get(cookieName)) {
        const oneYearAhead = new Date()
        oneYearAhead.setFullYear(oneYearAhead.getFullYear() + 1)
        Cookies.set(cookieName, value, { expires: oneYearAhead })
      }
    },
    [cookieName]
  )

  const clickButton = useCallback(() => {
    if (url) {
      setCookie(false)
      const isExternal = url.includes('http')

      if (isExternal) {
        history.push(url)
      } else {
        navigate(url)
      }
    }
    closeModal()
  }, [url, setCookie, history, navigate])

  const closeButtonClick = useCallback(() => {
    setCookie(false)
    history.push(pathname)
    closeModal()
  }, [setCookie, closeModal])

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setCookie(false)
        closeModal()
      }
    }

    window.addEventListener('keydown', close)

    return () => {
      window.removeEventListener('keydown', close)
    }
  }, [setCookie, closeModal])

  useEffect(() => {
    let timer: number
    const isDelayNumeric = /^\d+$/.test(openDelay)
    if (hash === '#popupContent') {
      openModal()
      setModalWillOpen(true)
    } else if (isDelayNumeric && !hash) {
      const delay = parseInt(openDelay)
      timer = window.setTimeout(() => {
        openModal()
      }, delay)
      setModalWillOpen(true)
    }

    return () => {
      timer && clearTimeout(timer)
    }
  }, [hash, openDelay, openModal])

  useEffect(() => {
    if (!hash && Cookies.get(cookieName)) {
      setModalWillOpen(false)
    }

    if (pageDesignerEditMode) {
      setModalWillOpen(false)
    }

    if (Cookies.get('appMode') === '1') {
      setModalWillOpen(false)
    }
  }, [hash, cookieName, pageDesignerEditMode])

  return modalWillOpen ? (
    <Modal
      style='shadow-drop w-11/12 max-w-xl p-0 md:w-full'
      isOpen={isModalOpen}
      onClose={closeModal}
      onCloseButtonClick={closeButtonClick}
      overlay
      closeOnOverlayClick
      blockScroll
      overlayClassName='extendedContainer'
      modalBody={
        <div className='h-full scroll-py-4 overflow-y-auto'>
          {custom?.content?.image && (
            <Image
              src={custom?.content?.image?.path}
              alt={imageAlt || ''}
              loading='eager'
              width={image.meta_data.width.toString()}
              height={image.meta_data.height.toString()}
              className='left-0 top-0 z-0 w-full object-cover md:h-80'
            />
          )}
          <div className='p-6 pt-5 lg:p-10 lg:pt-5'>
            {headline && (
              <Headline tag='h3' className='pb-1' children={headline} />
            )}
            {description && (
              <p className='text-16 font-Roboto'>{description}</p>
            )}
            {url && (
              <Button
                type='primary'
                children={buttonTitle}
                onClick={clickButton}
                className='mt-5'
              />
            )}
          </div>
        </div>
      }
    />
  ) : null
}

export default PopupContent
